import { api } from '../services/api';

export interface IAutentiqueSigner {
  nome: string;
  email: string;
}

export interface IAutentiqueEnvioData {
  card_id: number;
  anexo_id: number;
  nome_documento: string;
  signatarios: IAutentiqueSigner[];
}

export type AutentiqueSignatarioTipo = 'simples' | 'advogado';

/** Quem assina o contrato de evento, lido do documento. Lista de presença vem sem papel. */
export interface IAssinanteSugerido extends IAutentiqueSigner {
  papel: 'contratante' | 'contratada' | 'testemunha' | null;
  cpf?: string | null;
}

export interface IAutentiqueSignatarioStatus {
  id: number;
  nome: string;
  email: string;
  status: string;
  sign_link?: string;
  dt_assinatura?: string;
  // Papel/identificação do assinante (ex.: advogado tem cpf/oab).
  tipo?: AutentiqueSignatarioTipo;
  cpf?: string | null;
  oab?: string | null;
}

export interface IAutentiqueDocumentoStatus {
  id: number;
  autentique_document_id: string;
  nome: string;
  status: string;
  signatarios: IAutentiqueSignatarioStatus[];
  dt_criacao?: string;
}

export interface IAutentiqueEvento {
  tipo: string;
  descricao: string;
  dt: string | null;
  icone: 'check' | 'x';
  /** Só na recusa: o motivo que a pessoa escreveu. */
  motivo?: string | null;
}

export interface IAutentiqueSignatarioEventos {
  /** Linha nossa do assinante: sem ela (incluído no painel da Autentique) não há o que corrigir. */
  signatario_id?: number | null;
  nome: string;
  email: string;
  action: string;
  /** Link exclusivo do assinante (o backend gera para quem recebe por e-mail). */
  sign_link?: string | null;
  status: 'pendente' | 'assinado' | 'rejeitado';
  tipo?: AutentiqueSignatarioTipo;
  cpf?: string | null;
  oab?: string | null;
  eventos: IAutentiqueEvento[];
}

export interface IAutentiqueDocumentoEventos {
  documento_id: number;
  autentique_id?: string;
  /** A recusa de um não encerra este documento: quem falta ainda assina. */
  segue_na_recusa?: boolean;
  nome: string;
  status: string;
  arquivo_original?: string;
  arquivo_assinado?: string;
  dt_criacao?: string;
  signatarios: IAutentiqueSignatarioEventos[];
  erro?: string;
}

export const useAutentique = () => {
  const enviarParaAssinatura = async (data: IAutentiqueEnvioData) => {
    const res = await api.post('/api/v1/autentique/enviar/', data);
    return res.data;
  };

  const getStatus = async (cardId: number) => {
    const res = await api.get<{ data: IAutentiqueDocumentoStatus[] }>(
      `/api/v1/autentique/cards/${cardId}/status/`,
    );
    return res.data;
  };

  const getEventos = async (cardId: number) => {
    const res = await api.get<{ data: IAutentiqueDocumentoEventos[] }>(
      `/api/v1/autentique/cards/${cardId}/eventos/`,
    );
    return res.data;
  };

  const cancelarPending = async (cardId: number) => {
    const res = await api.delete(`/api/v1/autentique/cards/${cardId}/pending/`);
    return res.data;
  };

  // Cancela o envio no Autentique (quem não assinou não assina mais) e volta o contrato para CONTRATO.
  const cancelarEnvio = async (cardId: number) => {
    const res = await api.post<{
      data: { cancelados: number; ja_assinaram: string[]; contrato_status: string };
    }>(`/api/v1/autentique/cards/${cardId}/cancelar-envio/`);
    return res.data;
  };

  // Troca o e-mail de quem não assinou, no mesmo documento: quem assinou não assina de novo.
  const corrigirSignatario = async (
    cardId: number,
    signatarioId: number,
    dados: IAutentiqueSigner,
  ) => {
    const res = await api.post(
      `/api/v1/autentique/cards/${cardId}/signatarios/${signatarioId}/corrigir/`,
      dados,
    );
    return res.data;
  };

  const reenviarPendentes = async (cardId: number) => {
    const res = await api.post(`/api/v1/autentique/cards/${cardId}/reenviar-pendentes/`);
    return res.data;
  };

  // PDF do relatório de assinaturas do contrato de evento (o mesmo do e-mail ao supervisor). Nome do arquivo pelo
  // header: `filename*` traz o acento; sem header (proxy que filtra), um nome genérico.
  const baixarRelatorioPdf = async (cardId: number) => {
    const res = await api.get<Blob>(`/api/v1/autentique/cards/${cardId}/relatorio-pdf/`, {
      responseType: 'blob',
    });
    const cd = String(res.headers?.['content-disposition'] ?? '');
    const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(cd)?.[1];
    const nome =
      (utf8 && decodeURIComponent(utf8)) ||
      /filename="([^"]+)"/i.exec(cd)?.[1] ||
      'Relatorio de assinaturas.pdf';
    const url = window.URL.createObjectURL(res.data);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', nome);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  // Sincroniza no backend todos os documentos Autentique pendentes (recupera
  // webhooks perdidos). Pensado pra ser chamado ao abrir o board, sem bloquear.
  const syncPendentes = async () => {
    const res = await api.post('/api/v1/autentique/sync-pendentes');
    return res.data;
  };

  // Sugestão pro envio: quem assina o contrato de evento do card (vazio se não houver).
  // Sem `anexoId`, a rota escolhe entre os anexos do card.
  const getAssinantesSugeridos = async (cardId: number, anexoId?: number) => {
    const res = await api.get<{
      data: { assinantes: IAssinanteSugerido[]; documento: string | null };
    }>(`/api/v1/eventos/contrato-por-card/${cardId}/assinantes`, {
      params: { anexo_id: anexoId },
    });
    return {
      assinantes: res.data?.data?.assinantes ?? [],
      documento: res.data?.data?.documento ?? null,
    };
  };

  return {
    enviarParaAssinatura,
    getStatus,
    getEventos,
    cancelarPending,
    cancelarEnvio,
    corrigirSignatario,
    reenviarPendentes,
    baixarRelatorioPdf,
    syncPendentes,
    getAssinantesSugeridos,
  };
};
